import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Autocomplete,
} from "@mui/material";
import {
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  Pending as PendingIcon,
  Computer as HardwareIcon,
  Cloud as AzureIcon,
  Storage as SharePointIcon,
  PictureAsPdf as PdfIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import api from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import { formatUkDate } from "../utils/dateLocale";

const Attestation = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [assets, setAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [currentQuarter, setCurrentQuarter] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [comments, setComments] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [adminView, setAdminView] = useState(false);
  const [allAttestations, setAllAttestations] = useState([]);
  const [groupedByOwner, setGroupedByOwner] = useState([]);
  const [unassignedAssets, setUnassignedAssets] = useState([]);
  const [adminTab, setAdminTab] = useState(0);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assetToAssign, setAssetToAssign] = useState(null);
  const [assignOwnerEmail, setAssignOwnerEmail] = useState("");
  const [assignOwnerName, setAssignOwnerName] = useState("");
  const [entraidUsers, setEntraidUsers] = useState([]);
  const [activeActions, setActiveActions] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      if (isAdmin && adminView) {
        // Admin view - get all assets grouped by owner and attestations
        const [quarterRes, assetsRes, attestationsRes, usersRes] =
          await Promise.all([
            api.get("/api/attestation/current-quarter"),
            api.get("/api/attestation/all-assets"),
            api.get("/api/attestation/all"),
            api
              .get("/api/asset-register/users/entraid", {
                params: { configId: 1 }, // Use first config, or get from settings
              })
              .catch(() => ({ data: { users: [] } })),
          ]);
        setCurrentQuarter(quarterRes.data);
        setGroupedByOwner(assetsRes.data.groupedByOwner || []);
        setUnassignedAssets(assetsRes.data.unassignedAssets || []);
        setAllAttestations(attestationsRes.data.attestations || []);
        setEntraidUsers(usersRes.data.users || []);
      } else {
        // Standard user view
        const [assetsRes, statsRes, quarterRes] = await Promise.all([
          api.get("/api/attestation/my-assets", {
            params: statusFilter !== "all" ? { status: statusFilter } : {},
          }),
          api.get("/api/attestation/my-stats"),
          api.get("/api/attestation/current-quarter"),
        ]);
        setAssets(assetsRes.data.assets || []);
        setStats(statsRes.data);
        setCurrentQuarter(quarterRes.data);
      }
    } catch (error) {
      console.error("Failed to fetch attestation data:", error);
      setError("Failed to load attestation data");
    } finally {
      setLoading(false);
    }
  }, [isAdmin, adminView, statusFilter]);

  const fetchActiveActions = useCallback(async () => {
    try {
      const response = await api.get(
        "/api/attestation-scheduling/active-actions",
      );
      setActiveActions(response.data);
    } catch (error) {
      console.error("Failed to fetch active actions:", error);
    }
  }, []);

  useEffect(() => {
    fetchData();
    if (!isAdmin || !adminView) {
      fetchActiveActions();
    }
  }, [statusFilter, adminView, isAdmin, fetchData, fetchActiveActions]);

  const fetchHistory = async (assetId) => {
    try {
      const response = await api.get(
        `/api/attestation/asset/${assetId}/history`,
      );
      setHistory(response.data.history || []);
    } catch (error) {
      console.error("Failed to fetch history:", error);
    }
  };

  const handleConfirmReceipt = async (assetId) => {
    try {
      await api.post(`/api/attestation-scheduling/confirm-receipt/${assetId}`, {
        confirmedBy: `${user.first_name} ${user.last_name} (${user.email})`,
      });
      await fetchActiveActions();
      await fetchData();
    } catch (error) {
      console.error("Failed to confirm receipt:", error);
      setError("Failed to confirm device receipt");
    }
  };

  const handleConfirmRecertification = async (assetId) => {
    try {
      await api.post(
        `/api/attestation-scheduling/confirm-recertification/${assetId}`,
        {
          confirmedBy: `${user.first_name} ${user.last_name} (${user.email})`,
        },
      );
      await fetchActiveActions();
      await fetchData();
    } catch (error) {
      console.error("Failed to confirm recertification:", error);
      setError("Failed to confirm recertification");
    }
  };

  const handleAcknowledgeBYOD = async (assetId) => {
    try {
      await api.post(`/api/attestation-scheduling/acknowledge-byod/${assetId}`);
      await fetchActiveActions();
      await fetchData();
    } catch (error) {
      console.error("Failed to acknowledge BYOD:", error);
      setError("Failed to acknowledge BYOD security requirements");
    }
  };

  const handleAssetSelect = async (asset) => {
    setSelectedAsset(asset);
    await fetchHistory(asset.id);
  };

  const handleAttest = async (status) => {
    if (!selectedAsset) return;

    try {
      setSubmitting(true);
      await api.post(`/api/attestation/asset/${selectedAsset.id}/attest`, {
        status,
        comments: comments?.trim() || "",
      });

      setComments("");
      await fetchData();
      if (selectedAsset) {
        await fetchHistory(selectedAsset.id);
        // Refresh selected asset
        const updatedAssets = await api.get("/api/attestation/my-assets");
        const updated = updatedAssets.data.assets.find(
          (a) => a.id === selectedAsset.id,
        );
        if (updated) setSelectedAsset(updated);
      }
    } catch (error) {
      console.error("Failed to attest asset:", error);
      setError(error.response?.data?.error || "Failed to update attestation");
    } finally {
      setSubmitting(false);
    }
  };

  const getAssetTypeIcon = (asset) => {
    if (asset.is_azure_resource) return <AzureIcon />;
    if (asset.is_sharepoint_site) return <SharePointIcon />;
    return <HardwareIcon />;
  };

  const getAssetTypeLabel = (asset) => {
    if (asset.is_azure_resource) return "Azure";
    if (asset.is_sharepoint_site) return "SharePoint";
    return "Hardware";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "success";
      case "rejected":
        return "error";
      default:
        return "warning";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <ApprovedIcon />;
      case "rejected":
        return <RejectedIcon />;
      default:
        return <PendingIcon />;
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        {isAdmin && (
          <Button
            variant={adminView ? "contained" : "outlined"}
            onClick={() => {
              setAdminView(!adminView);
              setSelectedAsset(null);
            }}
          >
            {adminView ? "My Assets" : "Admin View"}
          </Button>
        )}
        {!isAdmin && (
          <Typography variant="body2" color="text.secondary">
            Viewing your assets only
          </Typography>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Active Actions Section */}
      {!isAdmin && activeActions && (
        <Card sx={{ mb: 3, bgcolor: "background.paper" }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Active Actions Required
            </Typography>
            <Grid container spacing={2}>
              {activeActions.receiptRequired &&
                activeActions.receiptRequired.length > 0 && (
                  <Grid item xs={12}>
                    <Alert severity="info" sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Device Receipt Confirmation Required (
                        {activeActions.receiptRequired.length})
                      </Typography>
                      {activeActions.receiptRequired.map((asset) => (
                        <Box
                          key={asset.id}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mt: 1,
                          }}
                        >
                          <Typography variant="body2">
                            {asset.asset_name || asset.asset_tag} (
                            {asset.device_type || asset.asset_type})
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleConfirmReceipt(asset.id)}
                          >
                            Confirm Receipt
                          </Button>
                        </Box>
                      ))}
                    </Alert>
                  </Grid>
                )}
              {activeActions.recertificationRequired &&
                activeActions.recertificationRequired.length > 0 && (
                  <Grid item xs={12}>
                    <Alert severity="warning" sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Recertification Required (
                        {activeActions.recertificationRequired.length})
                      </Typography>
                      {activeActions.recertificationRequired.map((asset) => (
                        <Box
                          key={asset.id}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mt: 1,
                          }}
                        >
                          <Typography variant="body2">
                            {asset.asset_name || asset.asset_tag} (
                            {asset.device_type || asset.asset_type})
                            {asset.next_recertification_date && (
                              <Typography
                                variant="caption"
                                display="block"
                                color="textSecondary"
                              >
                                Due: {formatUkDate(asset.next_recertification_date)}
                              </Typography>
                            )}
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            color="warning"
                            onClick={() =>
                              handleConfirmRecertification(asset.id)
                            }
                          >
                            Confirm Still Have Device
                          </Button>
                        </Box>
                      ))}
                    </Alert>
                  </Grid>
                )}
              {activeActions.byodAcknowledgmentRequired &&
                activeActions.byodAcknowledgmentRequired.length > 0 && (
                  <Grid item xs={12}>
                    <Alert severity="error" sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        BYOD Security Acknowledgment Required (
                        {activeActions.byodAcknowledgmentRequired.length})
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        As a contractor, you must acknowledge BYOD security
                        requirements for your devices.
                      </Typography>
                      {activeActions.byodAcknowledgmentRequired.map((asset) => (
                        <Box
                          key={asset.id}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mt: 1,
                          }}
                        >
                          <Typography variant="body2">
                            {asset.asset_name || asset.asset_tag} (
                            {asset.device_type || asset.asset_type})
                          </Typography>
                          <Button
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={() => handleAcknowledgeBYOD(asset.id)}
                          >
                            Acknowledge Security Requirements
                          </Button>
                        </Box>
                      ))}
                    </Alert>
                  </Grid>
                )}
              {activeActions.pendingAttestations &&
                activeActions.pendingAttestations.length > 0 && (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      <Typography variant="subtitle2" gutterBottom>
                        Pending Attestations (
                        {activeActions.pendingAttestations.length})
                      </Typography>
                      <Typography variant="body2">
                        You have {activeActions.pendingAttestations.length}{" "}
                        pending attestation(s) to review. Please review them
                        below.
                      </Typography>
                    </Alert>
                  </Grid>
                )}
            </Grid>
          </CardContent>
        </Card>
      )}

      {currentQuarter && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Current Review Period: Q{currentQuarter.quarter} {currentQuarter.year}
        </Alert>
      )}

      {!isAdmin && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You are viewing your assigned assets only. Contact an administrator if
          you need access to additional assets.
        </Alert>
      )}

      {isAdmin && adminView ? (
        // Admin View
        <Box>
          <Tabs
            value={adminTab}
            onChange={(e, v) => setAdminTab(v)}
            sx={{ mb: 3 }}
          >
            <Tab label="All Assets" />
            <Tab label="Attestations" />
            <Tab label="Summary Report" />
          </Tabs>

          {adminTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      All Assets by Owner
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {groupedByOwner.length} owner(s) with assigned assets
                      {unassignedAssets.length > 0 &&
                        `, ${unassignedAssets.length} unassigned`}
                    </Typography>

                    {/* Assets by Owner */}
                    {groupedByOwner.map((ownerGroup, idx) => (
                      <Box key={idx} mb={3}>
                        <Typography variant="subtitle1" gutterBottom>
                          {ownerGroup.ownerName}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          gutterBottom
                        >
                          {ownerGroup.ownerEmail} ({ownerGroup.assets.length}{" "}
                          assets)
                        </Typography>
                        <List>
                          {ownerGroup.assets.map((asset) => (
                            <React.Fragment key={asset.id}>
                              <ListItem
                                button
                                selected={selectedAsset?.id === asset.id}
                                onClick={() => handleAssetSelect(asset)}
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  width="100%"
                                >
                                  {getAssetTypeIcon(asset)}
                                  <Box ml={2} flex={1}>
                                    <ListItemText
                                      primary={
                                        asset.asset_name ||
                                        asset.device_type ||
                                        "Unnamed Asset"
                                      }
                                      secondary={getAssetTypeLabel(asset)}
                                    />
                                    <Chip
                                      label={
                                        asset.attestation_status || "pending"
                                      }
                                      color={getStatusColor(
                                        asset.attestation_status || "pending",
                                      )}
                                      size="small"
                                      sx={{ mt: 0.5 }}
                                    />
                                  </Box>
                                </Box>
                              </ListItem>
                              <Divider />
                            </React.Fragment>
                          ))}
                        </List>
                      </Box>
                    ))}

                    {/* Unassigned Assets Section */}
                    {unassignedAssets.length > 0 && (
                      <Box mb={3}>
                        <Typography
                          variant="subtitle2"
                          color="error"
                          gutterBottom
                        >
                          Unassigned Assets ({unassignedAssets.length})
                        </Typography>
                        <List>
                          {unassignedAssets.map((asset) => (
                            <React.Fragment key={asset.id}>
                              <ListItem
                                button
                                selected={selectedAsset?.id === asset.id}
                                onClick={() => handleAssetSelect(asset)}
                              >
                                <Box
                                  display="flex"
                                  alignItems="center"
                                  width="100%"
                                >
                                  {getAssetTypeIcon(asset)}
                                  <Box ml={2} flex={1}>
                                    <ListItemText
                                      primary={
                                        asset.asset_name ||
                                        asset.device_type ||
                                        "Unnamed Asset"
                                      }
                                      secondary={getAssetTypeLabel(asset)}
                                    />
                                    <Button
                                      size="small"
                                      startIcon={<PersonAddIcon />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setAssetToAssign(asset);
                                        setAssignDialogOpen(true);
                                      }}
                                      sx={{ mt: 0.5 }}
                                    >
                                      Assign Owner
                                    </Button>
                                  </Box>
                                </Box>
                              </ListItem>
                              <Divider />
                            </React.Fragment>
                          ))}
                        </List>
                      </Box>
                    )}

                    {groupedByOwner.length === 0 &&
                      unassignedAssets.length === 0 && (
                        <Typography variant="body2" color="text.secondary">
                          No assets found
                        </Typography>
                      )}
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={8}>
                {selectedAsset ? (
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Asset Review Workflow
                      </Typography>

                      {/* Asset Details */}
                      <Box mb={3}>
                        <Typography variant="subtitle1" gutterBottom>
                          Asset Details
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Name
                            </Typography>
                            <Typography variant="body1">
                              {selectedAsset.asset_name ||
                                selectedAsset.device_type ||
                                "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Type
                            </Typography>
                            <Typography variant="body1">
                              {getAssetTypeLabel(selectedAsset)}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Owner
                            </Typography>
                            <Typography variant="body1">
                              {selectedAsset.employee_name || "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Owner Email
                            </Typography>
                            <Typography variant="body1">
                              {selectedAsset.employee_email || "N/A"}
                            </Typography>
                          </Grid>
                          {selectedAsset.employee_name_secondary && (
                            <>
                              <Grid item xs={6}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Secondary Owner
                                </Typography>
                                <Typography variant="body1">
                                  {selectedAsset.employee_name_secondary}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Secondary Owner Email
                                </Typography>
                                <Typography variant="body1">
                                  {selectedAsset.employee_email_secondary ||
                                    "N/A"}
                                </Typography>
                              </Grid>
                            </>
                          )}
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Classification
                            </Typography>
                            <Typography variant="body1">
                              {selectedAsset.classification || "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={6}>
                            <Typography variant="body2" color="text.secondary">
                              Lifecycle Stage
                            </Typography>
                            <Typography variant="body1">
                              {selectedAsset.lifecycle_stage || "N/A"}
                            </Typography>
                          </Grid>
                          {selectedAsset.location && (
                            <Grid item xs={12}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                Location/URL
                              </Typography>
                              <Typography variant="body1">
                                {selectedAsset.location}
                              </Typography>
                            </Grid>
                          )}
                        </Grid>
                      </Box>

                      <Divider sx={{ my: 3 }} />

                      {/* Timeline */}
                      <Box mb={3}>
                        <Typography variant="subtitle1" gutterBottom>
                          Review History
                        </Typography>
                        {history.length > 0 ? (
                          <Box>
                            {history.map((item, index) => (
                              <Box key={item.id} display="flex" mb={2}>
                                <Box
                                  display="flex"
                                  flexDirection="column"
                                  alignItems="center"
                                  mr={2}
                                >
                                  <Avatar
                                    sx={{
                                      bgcolor:
                                        item.new_status === "approved"
                                          ? "success.main"
                                          : item.new_status === "rejected"
                                            ? "error.main"
                                            : "warning.main",
                                      width: 40,
                                      height: 40,
                                    }}
                                  >
                                    {getStatusIcon(
                                      item.new_status || item.action,
                                    )}
                                  </Avatar>
                                  {index < history.length - 1 && (
                                    <Box
                                      sx={{
                                        width: 2,
                                        height: 40,
                                        bgcolor: "divider",
                                        mt: 1,
                                      }}
                                    />
                                  )}
                                </Box>
                                <Box flex={1}>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {format(
                                      new Date(item.created_at),
                                      "dd/MM/yyyy HH:mm",
                                    )}
                                  </Typography>
                                  <Typography variant="body1">
                                    {item.reviewer_name} - {item.action}
                                  </Typography>
                                  {item.comments && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{ mt: 0.5 }}
                                    >
                                      {item.comments}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                          <Box display="flex">
                            <Box
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              mr={2}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: "grey.400",
                                  width: 40,
                                  height: 40,
                                }}
                              >
                                <PendingIcon />
                              </Avatar>
                            </Box>
                            <Box flex={1}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                No review history yet
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent>
                      <Typography
                        variant="body1"
                        color="text.secondary"
                        align="center"
                      >
                        Select an asset from the list to view details
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </Grid>
            </Grid>
          )}

          {adminTab === 1 && (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Asset Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Owner</TableCell>
                    <TableCell>Reviewer</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Period</TableCell>
                    <TableCell>Reviewed At</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allAttestations.map((att) => (
                    <TableRow key={att.id}>
                      <TableCell>{att.asset_name || "N/A"}</TableCell>
                      <TableCell>
                        {att.is_azure_resource
                          ? "Azure"
                          : att.is_sharepoint_site
                            ? "SharePoint"
                            : att.device_type}
                      </TableCell>
                      <TableCell>{att.employee_name || "N/A"}</TableCell>
                      <TableCell>{att.reviewer_name || "N/A"}</TableCell>
                      <TableCell>
                        <Chip
                          label={att.status}
                          color={getStatusColor(att.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        Q{att.review_period_quarter} {att.review_period_year}
                      </TableCell>
                      <TableCell>
                        {att.reviewed_at
                          ? format(
                              new Date(att.reviewed_at),
                              "dd/MM/yyyy HH:mm",
                            )
                          : "N/A"}
                      </TableCell>
                    </TableRow>
                  ))}
                  {allAttestations.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No attestations found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {adminTab === 2 && (
            <Card>
              <CardContent>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  mb={2}
                >
                  <Typography variant="h6">
                    Attestation Summary Report
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<PdfIcon />}
                    onClick={async () => {
                      try {
                        const response = await api.get(
                          "/api/attestation/summary/export",
                          {
                            responseType: "blob",
                          },
                        );

                        // Create a blob URL and trigger download
                        const url = window.URL.createObjectURL(
                          new Blob([response.data]),
                        );
                        const link = document.createElement("a");
                        link.href = url;
                        const quarter =
                          currentQuarter?.quarter || new Date().getMonth() + 1;
                        const year =
                          currentQuarter?.year || new Date().getFullYear();
                        link.setAttribute(
                          "download",
                          `attestation-summary-q${quarter}-${year}.pdf`,
                        );
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                      } catch (error) {
                        console.error("Failed to export PDF:", error);
                        alert("Failed to export PDF report. Please try again.");
                      }
                    }}
                  >
                    Export PDF
                  </Button>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Generate a comprehensive PDF report of all attestation
                  activities for the current review period. The report includes
                  summary statistics, assets grouped by owner, and detailed
                  attestation status.
                </Typography>
                {currentQuarter && (
                  <Typography variant="body2" color="text.secondary">
                    Current Review Period: Q{currentQuarter.quarter}{" "}
                    {currentQuarter.year}
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {/* Assign Owner Dialog */}
          <Dialog
            open={assignDialogOpen}
            onClose={() => setAssignDialogOpen(false)}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>Assign Asset Owner</DialogTitle>
            <DialogContent>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Asset:{" "}
                  {assetToAssign?.asset_name ||
                    assetToAssign?.device_type ||
                    "Unnamed Asset"}
                </Typography>
                <Autocomplete
                  options={entraidUsers}
                  getOptionLabel={(option) =>
                    `${option.displayName || option.name} (${option.mail || option.userPrincipalName})`
                  }
                  value={
                    entraidUsers.find((u) => u.mail === assignOwnerEmail) ||
                    null
                  }
                  onChange={(e, newValue) => {
                    if (newValue) {
                      setAssignOwnerEmail(
                        newValue.mail || newValue.userPrincipalName,
                      );
                      setAssignOwnerName(newValue.displayName || newValue.name);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Asset Owner"
                      placeholder="Search for user..."
                      fullWidth
                      sx={{ mt: 2 }}
                    />
                  )}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={async () => {
                  if (!assetToAssign || !assignOwnerEmail || !assignOwnerName)
                    return;

                  try {
                    const selectedUser = entraidUsers.find(
                      (u) =>
                        u.mail === assignOwnerEmail ||
                        u.userPrincipalName === assignOwnerEmail,
                    );
                    await api.put(
                      `/api/attestation/asset/${assetToAssign.id}/assign-owner`,
                      {
                        employeeId: selectedUser?.id || assignOwnerEmail,
                        employeeEmail: assignOwnerEmail,
                        employeeName: assignOwnerName,
                      },
                    );

                    setAssignDialogOpen(false);
                    setAssetToAssign(null);
                    setAssignOwnerEmail("");
                    setAssignOwnerName("");
                    await fetchData();
                  } catch (error) {
                    console.error("Failed to assign owner:", error);
                    setError(
                      error.response?.data?.error ||
                        "Failed to assign asset owner",
                    );
                  }
                }}
                disabled={!assignOwnerEmail || !assignOwnerName}
              >
                Assign
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      ) : (
        // Standard User View
        <Grid container spacing={3}>
          {/* Status Tiles */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              {stats && (
                <>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                          Total Assets
                        </Typography>
                        <Typography variant="h4">{stats.total}</Typography>
                        <Box mt={2}>
                          <Grid container spacing={1}>
                            <Grid item xs={4}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Hardware: {stats.byType.hardware.total}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Azure: {stats.byType.azure.total}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                SharePoint: {stats.byType.sharepoint.total}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                          Pending Review
                        </Typography>
                        <Typography variant="h4" color="warning.main">
                          {stats.pending}
                        </Typography>
                        <Box mt={2}>
                          <Grid container spacing={1}>
                            <Grid item xs={4}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                H: {stats.byType.hardware.pending}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                A: {stats.byType.azure.pending}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                S: {stats.byType.sharepoint.pending}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                          Approved
                        </Typography>
                        <Typography variant="h4" color="success.main">
                          {stats.approved}
                        </Typography>
                        <Box mt={2}>
                          <Grid container spacing={1}>
                            <Grid item xs={4}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                H: {stats.byType.hardware.approved}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                A: {stats.byType.azure.approved}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                S: {stats.byType.sharepoint.approved}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card>
                      <CardContent>
                        <Typography color="text.secondary" gutterBottom>
                          Rejected
                        </Typography>
                        <Typography variant="h4" color="error.main">
                          {stats.rejected}
                        </Typography>
                        <Box mt={2}>
                          <Grid container spacing={1}>
                            <Grid item xs={4}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                H: {stats.byType.hardware.rejected}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                A: {stats.byType.azure.rejected}
                              </Typography>
                            </Grid>
                            <Grid item xs={4}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                S: {stats.byType.sharepoint.rejected}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                </>
              )}
            </Grid>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  My Assets
                </Typography>
                <Box mb={2}>
                  <Button
                    size="small"
                    variant={statusFilter === "all" ? "contained" : "outlined"}
                    onClick={() => setStatusFilter("all")}
                    sx={{ mr: 1 }}
                  >
                    All
                  </Button>
                  <Button
                    size="small"
                    variant={
                      statusFilter === "pending" ? "contained" : "outlined"
                    }
                    onClick={() => setStatusFilter("pending")}
                    sx={{ mr: 1 }}
                  >
                    Pending
                  </Button>
                  <Button
                    size="small"
                    variant={
                      statusFilter === "approved" ? "contained" : "outlined"
                    }
                    onClick={() => setStatusFilter("approved")}
                    sx={{ mr: 1 }}
                  >
                    Approved
                  </Button>
                  <Button
                    size="small"
                    variant={
                      statusFilter === "rejected" ? "contained" : "outlined"
                    }
                    onClick={() => setStatusFilter("rejected")}
                  >
                    Rejected
                  </Button>
                </Box>
                <List>
                  {assets.map((asset) => (
                    <React.Fragment key={asset.id}>
                      <ListItem
                        button
                        selected={selectedAsset?.id === asset.id}
                        onClick={() => handleAssetSelect(asset)}
                      >
                        <Box display="flex" alignItems="center" width="100%">
                          {getAssetTypeIcon(asset)}
                          <Box ml={2} flex={1}>
                            <ListItemText
                              primary={
                                asset.asset_name ||
                                asset.device_type ||
                                "Unnamed Asset"
                              }
                              secondary={getAssetTypeLabel(asset)}
                            />
                            <Chip
                              label={asset.attestation_status || "pending"}
                              color={getStatusColor(
                                asset.attestation_status || "pending",
                              )}
                              size="small"
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        </Box>
                      </ListItem>
                      <Divider />
                    </React.Fragment>
                  ))}
                  {assets.length === 0 && (
                    <ListItem>
                      <ListItemText primary="No assets found" />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            {selectedAsset ? (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Asset Review Workflow
                  </Typography>

                  {/* Asset Details */}
                  <Box mb={3}>
                    <Typography variant="subtitle1" gutterBottom>
                      Asset Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Name
                        </Typography>
                        <Typography variant="body1">
                          {selectedAsset.asset_name ||
                            selectedAsset.device_type ||
                            "N/A"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Type
                        </Typography>
                        <Typography variant="body1">
                          {getAssetTypeLabel(selectedAsset)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Classification
                        </Typography>
                        <Typography variant="body1">
                          {selectedAsset.classification || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Lifecycle Stage
                        </Typography>
                        <Typography variant="body1">
                          {selectedAsset.lifecycle_stage || "N/A"}
                        </Typography>
                      </Grid>
                      {selectedAsset.location && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            Location/URL
                          </Typography>
                          <Typography variant="body1">
                            {selectedAsset.location}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* Timeline */}
                  <Box mb={3}>
                    <Typography variant="subtitle1" gutterBottom>
                      Review History
                    </Typography>
                    {history.length > 0 ? (
                      <Box>
                        {history.map((item, index) => (
                          <Box key={item.id} display="flex" mb={2}>
                            <Box
                              display="flex"
                              flexDirection="column"
                              alignItems="center"
                              mr={2}
                            >
                              <Avatar
                                sx={{
                                  bgcolor:
                                    item.new_status === "approved"
                                      ? "success.main"
                                      : item.new_status === "rejected"
                                        ? "error.main"
                                        : "warning.main",
                                  width: 40,
                                  height: 40,
                                }}
                              >
                                {getStatusIcon(item.new_status || item.action)}
                              </Avatar>
                              {index < history.length - 1 && (
                                <Box
                                  sx={{
                                    width: 2,
                                    height: 40,
                                    bgcolor: "divider",
                                    mt: 1,
                                  }}
                                />
                              )}
                            </Box>
                            <Box flex={1}>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {format(
                                  new Date(item.created_at),
                                  "dd/MM/yyyy HH:mm",
                                )}
                              </Typography>
                              <Typography variant="body1">
                                {item.reviewer_name} - {item.action}
                              </Typography>
                              {item.comments && (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ mt: 0.5 }}
                                >
                                  {item.comments}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Box display="flex">
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                          mr={2}
                        >
                          <Avatar
                            sx={{ bgcolor: "grey.400", width: 40, height: 40 }}
                          >
                            <PendingIcon />
                          </Avatar>
                        </Box>
                        <Box flex={1}>
                          <Typography variant="body2" color="text.secondary">
                            No review history yet
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>

                  <Divider sx={{ my: 3 }} />

                  {/* Review Actions */}
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Review & Approve
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Comments"
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      placeholder="Enter your review comments..."
                      sx={{ mb: 2 }}
                    />
                    <Box display="flex" gap={2}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<ApprovedIcon />}
                        onClick={() => handleAttest("approved")}
                        disabled={submitting}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<RejectedIcon />}
                        onClick={() => handleAttest("rejected")}
                        disabled={submitting}
                      >
                        Reject
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    align="center"
                  >
                    Select an asset from the list to review
                  </Typography>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default Attestation;
